/* Copyright (c) 2020-present CloudIO Inc */

import { ItemType } from '../../registry/ComponentRegistry';
import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './HTML';

const HTMLManager: ComponentManagerFn<'HTML'> = (
  platform: Platform,
): ComponentManager<'HTML'> => {
  return {
    ...baseComponentManager(platform, 'HTML'),
    defaultValue,
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    props: {
      text: {
        label: 'Text',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Specify html text.',
      },
      viewAttribute: {
        label: 'View Attribute',
        type: 'ViewAttributeChooser',
        required: true,
        aliasType: 'STORE',
        getAttributeDataTypes: () => 'ALL',
        preventMultiEdit: true,
      },
    },
    getDisplayText: (item) => item.props.text.value,
    category: 'Forms',
    icon: { iconName: 'html5', iconType: 'brands' },
    description: `**Overview**<br/>
The HTML component is a flexible rendering tool that allows you to inject raw HTML code directly into your application interface. It is used to display rich content, custom formatting, or embedded media that goes beyond the capabilities of standard text or layout components.
Use this component when you need to render dynamic templates, display formatted text from a database (like email bodies or blog posts), or embed third-party widgets (like maps or video players).

**1. Data Binding & Content**<br/>
The component offers two primary ways to source the HTML content it displays.
* **View Attribute:** Binds the component to a Data Store field. Use this when the HTML content is stored in your database (e.g., a "Description" field containing \`<b>Bold</b>\` text).
* **Text (Expression):** Allows you to construct HTML strings dynamically using an expression.
* **Example:** \` \`<div><h3>Welcome, \${$.user.firstName}</h3><p>Status: <strong>\${$.user.status}</strong></p></div>\` \`

**2. Rendering Behavior**<br/>
The component takes the provided string and parses it as standard HTML.
* **Sanitization:** Be aware that rendering raw HTML can have security implications (XSS). Ensure any user-generated content is sanitized.
* **Styling:** The injected HTML will inherit base application styles but can be styled inline (e.g., \`style="color: red;"\`) or via global CSS classes.

**3. Interactivity**<br/>
While primarily a display component, the HTML component supports basic user interaction.
* **TriggerOnClick:** You can attach a **TriggerOnClick** child component.
* **Function:** This turns the entire rendered HTML block into a clickable zone. This is useful for creating custom "Cards" or "Tiles" that look like complex HTML designs but act as navigation buttons.

**4. Best Use Cases**<br/>
* **Rich Text Display:** Rendering content created by a Rich Text Editor (e.g., formatted article bodies, email templates).
* **Custom Status Indicators:** Creating complex visual badges (e.g., a colored circle with text inside) using \`<div>\` and CSS logic.
* **Embeds:** Displaying IFrames or embed codes for external services (e.g., YouTube videos, Google Maps).
* **Dynamic Headers:** Creating a custom dashboard header that combines user data, images, and specific layout rules not possible with standard Label components.`,
  };
};

export default HTMLManager;
